/**
 * GET /client/{mcode}/subclients/{ccode}/products/available — picker row (camelCase).
 * Optional query `effectiveDate` (`yyyy-MM-dd`) filters rows as of that coverage day.
 */
export interface SubClientAvailableProductDto {
  clientProductId: number;
  productName: string;
  description: string | null;
  productType: string;
  clientContribution: number | null;
  memberContribution: number | null;
  monthlyRate: number | null;
  startDate: string | null;
  /** When present, preferred over `startDate` for "when coverage would begin" in the UI. */
  effectiveDate?: string | null;
  endDate: string | null;
  customClientProductName: string | null;
}

/**
 * POST /client/.../members/{subId}/products
 * Optional `effectiveDate` (yyyy-MM-dd): coverage starts on the first day of that month.
 * When omitted, API keeps legacy open-enrollment effective date.
 */
export interface AddMemberProductRequestDto {
  clientProductId: number;
  /** ISO date `yyyy-MM-dd`; day is normalized to the 1st of the month server-side. */
  effectiveDate?: string;
}

/**
 * PATCH .../members/{subId}/products/{memberProductId}/cancel
 * Stored cancellation date is set on the server to the current business day (America/New_York).
 * Coverage end is the last calendar day of `coverageEndDate`'s month.
 */
export type MemberProductCancellationReason = 0 | 1 | 2;

export interface CancelMemberProductRequestDto {
  cancellationReason: MemberProductCancellationReason;
  /** ISO date `yyyy-MM-dd`; day is normalized to the last day of the month server-side. Required. */
  coverageEndDate: string;
  confirmSeparation: boolean;
}

/** Single row in PATCH .../members/products/bulk-cancel `targets`. */
export interface BulkCancelMemberTargetDto {
  subId: number;
  cCode: string;
}

/**
 * PATCH /client/{mcode}/members/products/bulk-cancel — cancels all active member products per target (204 No Content).
 * Duplicate (subId, cCode) pairs are collapsed server-side.
 */
export interface BulkCancelMemberProductsRequestDto {
  targets: BulkCancelMemberTargetDto[];
  /** Optional; server defaults to 2 (Open enrollment) if omitted. */
  cancellationReason?: MemberProductCancellationReason;
  /** ISO date `yyyy-MM-dd`; day is normalized to the last day of the month server-side. Required. */
  coverageEndDate: string;
  confirmSeparation: boolean;
}

/** 0 = QLE, 1 = Separation, 2 = Open enrollment (API contract). */
export const MEMBER_PRODUCT_CANCELLATION_REASON_OPTIONS: ReadonlyArray<{
  value: MemberProductCancellationReason;
  label: string;
}> = [
  { value: 0, label: 'Qualifying life event' },
  { value: 1, label: 'Separation' },
  { value: 2, label: 'Open enrollment' }
];
